import type {
	ChunkedUploadDriver,
	ChunkedUploadDriverPorts,
	DriverFile,
	MultipartSessionShape,
	UploadLease,
	VerifyReinitializableResult
} from "./ports";
import { MultipartUploadGoneError, PersistedByteIntegrityError, UploadCancelledError, UploadOwnershipLostError } from "./uploadChunk";
import { resolveMultipartCompletionRequest } from "./completionTransition";
import { uploadPendingChunks, type UploadedPart } from "./driverChunkLoop";
import { createPartReceiptRecovery } from "./partReceiptRecovery";
import { runGuardedEffect } from "./ownershipGate";
import { preparePersistedUploadUrls } from "./driverUrlRefresh";
import { hasValidMultipartParts, isValidEtag } from "./multipartValidation";
import { CHUNK_SIZE } from "./chunkSize";
import {
	isChunkedObjectAssemblyIntegrityError,
	isUploadPartsIncompleteError,
	readUploadPartsIncompleteOrdinals
} from "./uploadFacadeErrors";
import { computeSlowTierRetryAt, MAX_UNKNOWN_DIAGNOSTIC_ATTEMPTS } from "./retryPolicy";
import {
	clearAssemblyIntegrityFailures,
	escalateUnproductivePartsIncomplete,
	recordAssemblyIntegrityFailure,
	recordPartsIncompleteRefusal
} from "./partsIncompleteBound";

type ResumeFileUploadInput<FileLike extends DriverFile, Session extends MultipartSessionShape, LeaseToken> = Parameters<
	ChunkedUploadDriver<FileLike, Session, LeaseToken>["resumeFileUpload"]
>[0];

interface ResumeFileUploadOptions<FileLike extends DriverFile, Session extends MultipartSessionShape, LeaseToken> {
	input: ResumeFileUploadInput<FileLike, Session, LeaseToken>;
	ports: ChunkedUploadDriverPorts<FileLike, Session, LeaseToken>;
	finalizeMultipartCompletion(input: { session: Session; parts: UploadedPart[]; lease: UploadLease<Session> }): Promise<boolean>;
}

/**
 * The ledger half of the chunked-object `UPLOAD_PARTS_INCOMPLETE` recovery, shared by every
 * completion catch that can see the refusal: the fresh `uploadFileDirect` upload, the persisted
 * resume, and `resumeFileUpload`'s own failure arm. Handling it in only one of them would leave the
 * others re-completing the identical incomplete manifest on every pass, forever.
 *
 * Guarded like every sibling durable write in those catches: a tab that has already lost the lease
 * must not flip another owner's chunk ledger back to "not uploaded".
 */
export async function applyPartsIncompleteRemark<FileLike extends DriverFile, Session extends MultipartSessionShape, LeaseToken>(input: {
	session: Session;
	error: unknown;
	lease: UploadLease<Session>;
	ports: ChunkedUploadDriverPorts<FileLike, Session, LeaseToken>;
}): Promise<{
	outcome: "applied" | "skipped-not-owned" | "no-remark-port";
	chunkIndexes: number[];
	slowTier: boolean;
	/**
	 * Did this round actually change the ledger? False when there is no `markChunksNotUploaded` port,
	 * when the server named nothing actionable, or when the store's CAS modified zero rows — three
	 * different ways of producing a byte-identical next `/complete`, all of which the parts-incomplete
	 * bound must count as unproductive. It previously reported "applied" for the first of them.
	 */
	remarkApplied: boolean;
}> {
	const { session, error, ports } = input;
	// `undefined` (no readable details) and `[]` (present but every entry malformed) are the same
	// verdict: nothing actionable to re-mark. The row is still released, and the next pass reconciles
	// against `/parts` and re-uploads whatever the server does not list.
	const ordinals = readUploadPartsIncompleteOrdinals(error) ?? [];
	const chunkIndexes = [
		...new Set(ordinals.map((ordinal) => ordinal - 1).filter((chunkIndex) => chunkIndex >= 0 && chunkIndex < session.totalChunks))
	];
	// A refusal the server keeps repeating would otherwise re-upload and re-complete on the fast tier
	// forever; this is the escalation `classifyFailure`'s "retry-slow" would have applied.
	const slowTier = (session.retryAttempt ?? 0) >= MAX_UNKNOWN_DIAGNOSTIC_ATTEMPTS;
	// Silent-failure rule: name the condition and the values, so a completion that keeps refusing is
	// diagnosable from this line alone.
	console.warn("[Upload] Chunked-object completion refused; re-marking the named parts for re-upload", {
		sessionId: session.id,
		registryId: session.registryId,
		attemptId: session.attemptId,
		retryAttempt: session.retryAttempt ?? 0,
		ordinals,
		chunkIndexes,
		slowTier,
		canReMark: Boolean(ports.sessionStore.markChunksNotUploaded)
	});
	if (!ports.sessionStore.markChunksNotUploaded) {
		// A DISTINCT outcome, not "applied": the app has no way to re-mark anything, so every round of
		// this refusal reproduces the identical `/complete` body however many ordinals the server names.
		// Reporting "applied" here reset the bound's streak on every round and made it unreachable.
		console.warn("[Upload] Chunked-object completion refused but this app has no chunk re-mark port; nothing can change", {
			sessionId: session.id,
			registryId: session.registryId,
			attemptId: session.attemptId,
			chunkIndexes
		});
		return { outcome: "no-remark-port", chunkIndexes, slowTier, remarkApplied: false };
	}
	if (chunkIndexes.length === 0) return { outcome: "applied", chunkIndexes, slowTier, remarkApplied: false };
	// `runGuardedEffect`'s outcome channel is string-only, so the row count comes back beside it.
	let modifiedRowCount: number | undefined;
	const writeOutcome = await runGuardedEffect(input.lease, session, async () => {
		const result = await ports.sessionStore.markChunksNotUploaded!(session, chunkIndexes);
		// `undefined` is a store that cannot count its own rows; only an explicit `0` is a refused CAS.
		modifiedRowCount = typeof result === "number" ? result : undefined;
	});
	if (writeOutcome === "skipped-not-owned") {
		console.warn("[Upload] Chunked-object re-mark skipped: the lease is owned by another tab", {
			sessionId: session.id,
			registryId: session.registryId,
			attemptId: session.attemptId,
			chunkIndexes
		});
		return { outcome: "skipped-not-owned", chunkIndexes, slowTier, remarkApplied: false };
	}
	if (modifiedRowCount === 0) {
		console.warn("[Upload] Chunked-object re-mark modified zero rows; the next completion body is unchanged", {
			sessionId: session.id,
			registryId: session.registryId,
			attemptId: session.attemptId,
			chunkIndexes
		});
	}
	return { outcome: "applied", chunkIndexes, slowTier, remarkApplied: modifiedRowCount !== 0 };
}

export function logResumeWarning(
	telemetry: ChunkedUploadDriverPorts<DriverFile, MultipartSessionShape, unknown>["telemetry"],
	event: string,
	context: Record<string, unknown>,
	error?: unknown
): void {
	try {
		if (error === undefined) {
			telemetry.logWarn(event, context);
		} else {
			telemetry.logWarn(event, context, error);
		}
	} catch {
		// Resume ownership and cleanup semantics remain fail-closed when telemetry fails.
	}
}

/**
 * A durable row's `totalChunks` is structured-clone data a PREVIOUS build wrote to IndexedDB, which
 * enforces no schema, so it can come back absent, fractional or zero. Both persisted-resume entry
 * points call this so neither can drift: `driver.resumePersistedMultipartSession` (before its
 * reconciliation probe, which would otherwise read a ListParts listing as handoff evidence for a row
 * whose own chunk count is unusable) and `resumeFileUpload` below, which the public
 * `driver.resumeFileUpload` both apps export reaches directly.
 *
 * `PersistedByteIntegrityError`, not a bare Error — see that class's own doc in `uploadChunk.ts`.
 */
export function assertUsablePersistedTotalChunks(
	session: Pick<MultipartSessionShape, "id" | "uploadId" | "fileName" | "totalChunks">,
	telemetry: ChunkedUploadDriverPorts<DriverFile, MultipartSessionShape, unknown>["telemetry"]
): void {
	if (Number.isInteger(session.totalChunks) && session.totalChunks > 0) return;
	// Silent-failure rule: on the background drain the parked row's reason string is otherwise the
	// only trace of why this session stopped.
	logResumeWarning(telemetry, "recording.fileUpload.invalidPersistedTotalChunks", {
		sessionId: session.id,
		uploadId: session.uploadId,
		totalChunks: session.totalChunks,
		fileName: session.fileName
	});
	throw new PersistedByteIntegrityError(`Invalid persisted multipart totalChunks: ${session.totalChunks}`);
}

async function ownsForEffect<FileLike extends DriverFile, Session extends MultipartSessionShape, LeaseToken>(
	lease: UploadLease<Session>,
	session: Session,
	ports: ChunkedUploadDriverPorts<FileLike, Session, LeaseToken>
): Promise<boolean> {
	try {
		return await lease.owns(session);
	} catch (error) {
		logResumeWarning(
			ports.telemetry,
			"recording.fileUpload.resumeOwnershipCheckFailed",
			{ sessionId: session.id, uploadId: session.uploadId },
			error
		);
		return false;
	}
}

async function hasNoCompetingOwner<FileLike extends DriverFile, Session extends MultipartSessionShape, LeaseToken>(
	options: ResumeFileUploadOptions<FileLike, Session, LeaseToken>,
	lease: UploadLease<Session>
): Promise<boolean> {
	if (await ownsForEffect(lease, options.input.session, options.ports)) return true;
	try {
		// A deleted row has no competing owner. Preserve the existing best-effort upload path:
		// the store's atomic merge is a no-op and the refreshed URL can still finish the PUT.
		return (await options.ports.sessionStore.getById(options.input.session.id)) === undefined;
	} catch (error) {
		logResumeWarning(
			options.ports.telemetry,
			"recording.fileUpload.resumeCompetingOwnerCheckFailed",
			{
				sessionId: options.input.session.id,
				uploadId: options.input.session.uploadId
			},
			error
		);
		return false;
	}
}

/**
 * Whether a gone-on-S3 multipart upload should be probed for an already-assembled object before the
 * failure is classified. Keeps the historical default (see `ChunkedUploadPolicies.goneMultipartRecovery`)
 * for every caller that does not state a preference.
 */
export function recoversGoneMultipartByProbe(policies: {
	lostRecoveryPolicy: "reinitialize" | "fail-fast";
	goneMultipartRecovery?: "probe-complete" | "none";
}): boolean {
	return policies.goneMultipartRecovery
		? policies.goneMultipartRecovery === "probe-complete"
		: policies.lostRecoveryPolicy === "fail-fast";
}

function originalTransientError(error: unknown): unknown {
	if (error instanceof Error && "cause" in error && (error as Error & { cause?: unknown }).cause !== undefined) {
		return (error as Error & { cause?: unknown }).cause;
	}
	return error;
}

async function releasePendingAfterFailure<FileLike extends DriverFile, Session extends MultipartSessionShape, LeaseToken>(
	options: ResumeFileUploadOptions<FileLike, Session, LeaseToken>,
	lease: UploadLease<Session>,
	error: unknown,
	classification: "lost" | "transient" | "fatal"
): Promise<never> {
	if (
		options.input.behavior?.markPendingOnError !== false &&
		!(classification === "lost" && options.ports.policies.lostRecoveryPolicy === "fail-fast")
	) {
		try {
			await runGuardedEffect(lease, options.input.session, () =>
				options.ports.sessionStore.releasePending(options.input.session, options.ports.errors.normalize(error).message)
			);
		} catch (pendingError) {
			logResumeWarning(
				options.ports.telemetry,
				`[Upload] Could not mark "${options.input.session.fileName}" pending after resume failure; preserving original error`,
				{
					sessionId: options.input.session.id,
					uploadId: options.input.session.uploadId
				},
				pendingError
			);
			if (classification === "transient") {
				throw originalTransientError(error);
			}
		}
	}
	throw error;
}

export async function recoverGoneMultipartForFailFastPolicy<
	FileLike extends DriverFile,
	Session extends MultipartSessionShape,
	LeaseToken
>(input: {
	session: Session;
	ports: ChunkedUploadDriverPorts<FileLike, Session, LeaseToken>;
	lease: UploadLease<Session>;
	parts: UploadedPart[];
	verifyReinitializable?: () => Promise<VerifyReinitializableResult>;
}): Promise<"completed" | "yielded"> {
	if (!(await ownsForEffect(input.lease, input.session, input.ports))) return "yielded";
	const hasNoPersistedParts =
		input.parts.length === 0 && !input.session.ephemeral && input.ports.policies.probeAlreadyCompleteWithNoParts === true;
	const partNumbers = new Set(input.parts.map((part) => part.PartNumber));
	const hasValidProbeParts =
		Number.isInteger(input.session.totalChunks) &&
		input.session.totalChunks > 0 &&
		input.parts.length > 0 &&
		input.parts.length <= input.session.totalChunks &&
		partNumbers.size === input.parts.length &&
		input.parts.every(
			(part) =>
				Number.isInteger(part.PartNumber) &&
				part.PartNumber >= 1 &&
				part.PartNumber <= input.session.totalChunks &&
				isValidEtag(part.ETag)
		);
	if (!hasNoPersistedParts && !hasValidProbeParts) {
		throw new Error(
			`Resume of "${input.session.fileName}" has ${input.parts.length}/${input.session.totalChunks} contiguous part ETags`
		);
	}
	const completionResult = await resolveMultipartCompletionRequest({
		completeMultipartUpload: () =>
			input.ports.multipartApi.complete(
				input.session,
				[...input.parts].sort((left, right) => left.PartNumber - right.PartNumber)
			),
		classifyCompletion: (error) => input.ports.errors.classifyCompletion(error),
		verifyReinitializable: () =>
			input.session.ephemeral
				? Promise.resolve("guaranteed" as const)
				: (input.verifyReinitializable?.() ?? input.ports.sessionStore.verifyReinitializable(input.session)),
		lostRecoveryPolicy: input.ports.policies.lostRecoveryPolicy,
		normalize: (error) => input.ports.errors.normalize(error)
	});
	if (!completionResult.alreadyComplete && !hasValidMultipartParts(input.parts, input.session.totalChunks)) {
		throw new Error(
			`Resume of "${input.session.fileName}" has ${input.parts.length}/${input.session.totalChunks} contiguous part ETags`
		);
	}

	return markPersistedSessionTerminalComplete(input);
}

/**
 * The lease-guarded "the object is on the server; retire this row" write pair.
 *
 * Extracted so the two callers that reach the SAME conclusion by different routes cannot drift on
 * what retiring a session means: `recoverGoneMultipartForFailFastPolicy` above (S3 said the MPU is
 * gone and the completion request came back already-complete) and the resume loop's
 * remote-complete probe in `chunkedUploadDriver` (ListParts answered `assembled`, so there is
 * nothing left to PUT or complete). A cleanup failure is logged and swallowed on purpose — the
 * durable terminal-complete write has already landed, so the row can never re-upload or
 * re-complete; only its local chunk bytes linger.
 */
export async function markPersistedSessionTerminalComplete<
	FileLike extends DriverFile,
	Session extends MultipartSessionShape,
	LeaseToken
>(input: {
	session: Session;
	ports: ChunkedUploadDriverPorts<FileLike, Session, LeaseToken>;
	lease: UploadLease<Session>;
}): Promise<"completed" | "yielded"> {
	// A completion that landed ends the assembly-integrity streak (finding 14). Cleared HERE rather
	// than at the `complete` call because this is the single point both success routes converge on,
	// so neither can retire a row while the ledger still holds failures against its attempt key.
	clearAssemblyIntegrityFailures(input.session);
	const terminalOutcome = await runGuardedEffect(input.lease, input.session, async () => {
		if (!input.session.ephemeral) {
			await input.ports.sessionStore.markTerminalComplete(input.session);
		}
		try {
			if (!input.session.ephemeral) {
				await input.ports.sessionStore.cleanupCompleted(input.session);
			}
		} catch (cleanupError) {
			logResumeWarning(
				input.ports.telemetry,
				`[Upload] "${input.session.fileName}" completed on S3 but IDB cleanup failed — session is durably complete and will not re-upload or re-complete`,
				{ sessionId: input.session.id, uploadId: input.session.uploadId },
				cleanupError
			);
		}
	});
	if (terminalOutcome === "skipped-not-owned") return "yielded";
	return "completed";
}

export async function resumeFileUpload<FileLike extends DriverFile, Session extends MultipartSessionShape, LeaseToken>(
	options: ResumeFileUploadOptions<FileLike, Session, LeaseToken>
): Promise<"completed" | "yielded"> {
	const { input, ports } = options;
	const leaseScope = input.leaseScope ?? ports.leaseCoordinator.createScope("resume", input.session.id);
	const managesLease = input.behavior?.skipLeaseManagement !== true;

	if (managesLease && !(await leaseScope.lease.claim(input.session))) {
		return "yielded";
	}
	if (managesLease) leaseScope.activate(input.session);

	const runUpload = async (): Promise<"completed" | "yielded"> => {
		const parts: UploadedPart[] = [];
		try {
			const preparedUrls = await preparePersistedUploadUrls({
				session: input.session,
				pendingChunkIndexes: input.unuploadedChunks.map((chunk) => chunk.chunkIndex),
				fallbackUrls: input.chunkUrls,
				ports,
				ownsCurrentSession: () => hasNoCompetingOwner(options, leaseScope.lease),
				lease: leaseScope.lease
			});
			ports.telemetry.logInfo("recording.fileUpload.resumeStarted", {
				sessionId: input.session.id,
				conversationId: input.session.conversationId,
				alreadyUploadedChunks: input.allChunks.filter((chunk) => chunk.isUploaded).length,
				pendingChunks: input.unuploadedChunks.length,
				totalChunks: input.session.totalChunks
			});

			const chunkLoopResult = await uploadPendingChunks({
				session: input.session,
				allChunks: input.allChunks,
				pendingChunks: input.unuploadedChunks,
				chunkUrls: preparedUrls.chunkUrls,
				chunkHeaders: preparedUrls.chunkHeaders,
				parts,
				chunkSources: input.chunkSource ? { forPersistedUpload: () => input.chunkSource! } : ports.chunkSources,
				transport: ports.chunkTransport,
				lease: leaseScope.lease,
				telemetry: ports.telemetry,
				chunkSize: CHUNK_SIZE,
				chunkConcurrency: ports.chunkConcurrency,
				maxChunkRetries: ports.maxChunkRetries,
				cancelSignal: input.cancelSignal,
				refreshPresignedUrl: preparedUrls.refreshPresignedUrl,
				checkpointChunk: (chunkIndex, etag) => ports.sessionStore.markChunkUploaded(input.session, chunkIndex, etag),
				// LSP-19201: a part S3 accepted whose ETag was stripped in transit is recovered from the server's
				// own manifest instead of being re-PUT forever. See partReceiptRecovery.ts.
				recoverPartReceipt: createPartReceiptRecovery({
					listParts: () =>
						ports.multipartApi.listParts(input.session, {
							lease: leaseScope.lease,
							ownsCurrentSession: () => hasNoCompetingOwner(options, leaseScope.lease)
						}),
					telemetry: ports.telemetry,
					context: () => ({
						sessionId: input.session.id,
						conversationId: input.session.conversationId,
						uploadId: input.session.uploadId,
						fileName: input.session.fileName
					})
				}),
				createChunkSourceError: (kind, fileName) => ports.errors.createChunkSourceError(kind, fileName),
				onCheckpointError: (error, chunkIndex) =>
					ports.telemetry.logError(
						"recording.fileUpload.resumeCheckpointFailed",
						{
							chunkIndex,
							sessionId: input.session.id,
							uploadId: input.session.uploadId
						},
						error
					),
				onLeaseTouchError: (error, chunkIndex) =>
					ports.telemetry.logWarn(
						"recording.fileUpload.resumeLeaseTouchFailed",
						{
							chunkIndex,
							sessionId: input.session.id,
							uploadId: input.session.uploadId
						},
						error
					),
				onChunkComplete: input.onChunkComplete,
				onProgress: input.onProgress,
				onActivity: input.onActivity,
				isOnline: ports.connectivity?.isOnline,
				waitForOnline: ports.connectivity?.waitForOnline,
				isPresignedUrlExpired: ports.isPresignedUrlExpired,
				retryDelay: ports.retryDelay,
				ensureOwnershipAfterRead: true
			});

			if (chunkLoopResult.outcome === "yielded") {
				console.warn("[Upload] Resume yielded: the chunk loop gave up ownership", {
					sessionId: input.session.id,
					uploadId: input.session.uploadId,
					chunksCompleted: chunkLoopResult.chunksCompleted,
					totalChunks: input.session.totalChunks
				});
				return "yielded";
			}
			if (!hasValidMultipartParts(parts, input.session.totalChunks)) {
				throw (
					input.createTruncatedPartsError?.(parts.length) ??
					new Error(
						`Resume of "${input.session.fileName}" has ${parts.length}/${input.session.totalChunks} contiguous part ETags`
					)
				);
			}

			if (ports.policies.cancelEnabled && input.cancelSignal?.aborted) {
				throw new UploadCancelledError(`Upload of "${input.session.fileName}" was cancelled`);
			}
			const completed = await options.finalizeMultipartCompletion({
				session: input.session,
				parts,
				lease: leaseScope.lease
			});
			return completed ? "completed" : "yielded";
		} catch (caughtError) {
			if (caughtError instanceof UploadOwnershipLostError) {
				console.warn("[Upload] Resume yielded: lease ownership lost", {
					sessionId: input.session.id,
					uploadId: input.session.uploadId,
					totalChunks: input.session.totalChunks
				});
				return "yielded";
			}
			if (caughtError instanceof UploadCancelledError) {
				const shouldTombstoneCancelled = input.behavior?.shouldTombstoneCancelled?.() ?? true;
				const superseded = input.behavior?.isSuperseded?.() ?? false;
				if (ports.policies.cancelEnabled && shouldTombstoneCancelled && !superseded) {
					await runGuardedEffect(leaseScope.lease, input.session, () =>
						ports.sessionStore.tombstoneCancelled(input.session)
					).catch((cleanupError) => {
						if (input.behavior?.isSuperseded?.()) return;
						logResumeWarning(
							ports.telemetry,
							"recording.fileUpload.cleanupFailed",
							{
								sessionId: input.session.id,
								uploadId: input.session.uploadId,
								operation: "tombstone-cancelled-session"
							},
							cleanupError
						);
					});
				}
				throw caughtError;
			}

			let effectiveError = caughtError;
			if (caughtError instanceof MultipartUploadGoneError && recoversGoneMultipartByProbe(ports.policies)) {
				try {
					return await recoverGoneMultipartForFailFastPolicy({
						session: input.session,
						ports,
						lease: leaseScope.lease,
						parts
					});
				} catch (recoveryError) {
					effectiveError = recoveryError;
				}
			}

			// Before the generic classifier: a parts-incomplete refusal is neither lost nor fatal, and
			// `releasePendingAfterFailure` would release the row with the ledger still claiming every
			// chunk is uploaded — so the next pass re-completes the identical manifest. Skipped when the
			// caller owns its own failure handling (`markPendingOnError: false`, which
			// `resumePersistedMultipartSession` uses): its outer catch runs this same recovery.
			if (isUploadPartsIncompleteError(effectiveError) && input.behavior?.markPendingOnError !== false) {
				const remark = await applyPartsIncompleteRemark({
					session: input.session,
					error: effectiveError,
					lease: leaseScope.lease,
					ports
				});
				if (remark.outcome === "skipped-not-owned") return "yielded";
				// Same bound as the driver's own arm: a refusal this client cannot act on must not be
				// released for an identical next pass forever.
				const verdict = recordPartsIncompleteRefusal({
					session: input.session,
					error: effectiveError,
					chunkIndexes: remark.chunkIndexes,
					remarkApplied: remark.remarkApplied
				});
				if (verdict.exhausted) {
					return escalateUnproductivePartsIncomplete({
						session: input.session,
						error: effectiveError,
						lease: leaseScope.lease,
						ports,
						verdict
					});
				}
				const releaseOutcome = await runGuardedEffect(leaseScope.lease, input.session, () =>
					ports.sessionStore.releasePending(
						input.session,
						ports.errors.normalize(effectiveError).message,
						remark.slowTier
							? { retryAt: computeSlowTierRetryAt({ attempt: input.session.retryAttempt ?? 0, nowMs: ports.now() }) }
							: undefined
					)
				);
				if (releaseOutcome === "skipped-not-owned") return "yielded";
				// Not rethrown: the row is pending with the owed chunks re-marked, so the drain's next
				// pass re-uploads them. Surfacing it as a generic failure would announce a dead upload.
				return "yielded";
			}
			// Finding 14's twin of the arm above: the integrity bound, for the lane whose caller does NOT
			// own its own failure handling. Same ledger, same escalation path.
			if (isChunkedObjectAssemblyIntegrityError(effectiveError) && input.behavior?.markPendingOnError !== false) {
				const verdict = recordAssemblyIntegrityFailure({ session: input.session, error: effectiveError });
				if (verdict.exhausted) {
					return escalateUnproductivePartsIncomplete({
						session: input.session,
						error: effectiveError,
						lease: leaseScope.lease,
						ports,
						verdict,
						lane: "assembly-integrity"
					});
				}
			}
			return releasePendingAfterFailure(options, leaseScope.lease, effectiveError, ports.errors.classifyCompletion(effectiveError));
		}
	};

	try {
		// Outside runUpload's own try on purpose: thrown from in there it would reach
		// releasePendingAfterFailure, which releases the row back to `pending` regardless of how
		// classifyCompletion reads it — an unbounded re-pick loop on a durable field no retry can repair.
		// Inside THIS try so the activation this function already took is still released symmetrically.
		assertUsablePersistedTotalChunks(input.session, ports.telemetry);
		return managesLease ? await leaseScope.runWithLiveness(input.session, runUpload) : await runUpload();
	} finally {
		if (managesLease) leaseScope.release(input.session);
	}
}
